'use client';

import Link from 'next/link';

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Trans } from '@kit/ui/trans';

import { PasswordSignUpSchema } from '../schemas/password-sign-up.schema';
import { EmailInput } from './email-input';
import { PasswordInput } from './password-input';
import { TermsAndConditionsFormField } from './terms-and-conditions-form-field';

interface PasswordSignUpFormProps {
  defaultValues?: {
    email: string;
  };

  displayTermsCheckbox?: boolean;

  onSubmit: (params: {
    email: string;
    password: string;
    repeatPassword: string;
  }) => unknown;

  loading: boolean;
  captchaLoading: boolean;
}

export function PasswordSignUpForm({
  defaultValues,
  displayTermsCheckbox,
  onSubmit,
  loading,
  captchaLoading,
}: PasswordSignUpFormProps) {
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(PasswordSignUpSchema),
    defaultValues: {
      email: defaultValues?.email ?? '',
      password: '',
      repeatPassword: '',
    },
  });

  return (
    <Form {...form}>
      {/* noValidate hands validation to the Zod resolver. Without it the
          browser's own bubble fires first on an empty email and the form's
          inline messages never get a chance to render.

          method="post" covers the case where this form ever submits natively —
          before hydration, or if the bundle fails. The default GET would put
          the password in the query string, and the browser's own `required`
          check is no longer there to stop an empty one. */}
      <form
        noValidate
        method={'post'}
        className={'flex w-full flex-col gap-y-4'}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className={'flex flex-col space-y-2.5'}>
          <FormField
            control={form.control}
            name={'email'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <EmailInput data-test={'email-input'} {...field} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'password'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PasswordInput
                    autoComplete="new-password"
                    minLength={8}
                    {...field}
                  />
                </FormControl>

                {/* The length rule was only in the Zod schema, so the first
                    anyone heard of it was an error under a password they had
                    already chosen. */}
                <FormDescription>
                  <Trans i18nKey={'auth:passwordDescription'} />
                </FormDescription>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'repeatPassword'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PasswordInput
                    data-test={'repeat-password-input'}
                    autoComplete="new-password"
                    minLength={8}
                    placeholder={t('auth:repeatPasswordPlaceholder')}
                    {...field}
                  />
                </FormControl>

                <FormDescription>
                  <Trans i18nKey={'auth:repeatPasswordDescription'} />
                </FormDescription>

                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <If condition={displayTermsCheckbox}>
          <TermsAndConditionsFormField />
        </If>

        <Button
          data-test={'auth-submit-button'}
          className={'w-full'}
          type="submit"
          disabled={loading || captchaLoading}
        >
          {/* These labels used to enter with `slide-in-from-bottom-24` — a 96px
              travel inside a 36px button, so for the first 150ms of every
              render the text sat outside the button and it painted as a blank
              block of colour. The arrow compounded it with a 500ms delay. Both
              are gone; the fade alone is enough. Defaults keep the label
              readable before the translation namespace resolves. */}
          <If condition={captchaLoading}>
            <span className={'animate-in fade-in'}>
              <Trans
                i18nKey={'auth:verifyingCaptcha'}
                defaults={"Checking you're not a robot..."}
              />
            </span>
          </If>

          <If condition={loading && !captchaLoading}>
            <span className={'animate-in fade-in'}>
              <Trans i18nKey={'auth:signingUp'} defaults={'Signing up...'} />
            </span>
          </If>

          <If condition={!loading && !captchaLoading}>
            <span className={'animate-in fade-in flex items-center'}>
              <Trans
                i18nKey={'auth:signUpWithEmail'}
                defaults={'Try Free For 14 Days'}
              />

              <ArrowRight
                className={
                  'zoom-in animate-in slide-in-from-left-2 fill-mode-both h-4'
                }
              />
            </span>
          </If>
        </Button>

        {/* When the checkbox is off, signup still creates a binding account —
            so the terms have to be linked somewhere on the form. */}
        <If condition={!displayTermsCheckbox}>
          <p className={'text-muted-foreground text-center text-xs'}>
            <Trans
              i18nKey={'auth:signUpTermsNotice'}
              components={{
                TermsOfServiceLink: (
                  <Link
                    target={'_blank'}
                    className={'underline'}
                    href={'/terms-of-service'}
                  >
                    <Trans i18nKey={'auth:termsOfService'} />
                  </Link>
                ),
                PrivacyPolicyLink: (
                  <Link
                    target={'_blank'}
                    className={'underline'}
                    href={'/privacy-policy'}
                  >
                    <Trans i18nKey={'auth:privacyPolicy'} />
                  </Link>
                ),
              }}
            />
          </p>
        </If>
      </form>
    </Form>
  );
}
